import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, EmptyState, ResponsiveDataView, SearchField, SegmentedTabs, Select, type DataColumn } from "@akuna/ui";
import { useTeamMembers } from "@/hooks/useTeam";
import { isActiveMember, type TeamMemberRow } from "@/lib/team/types";
import { getInitials } from "@/lib/utils/string-utils";
import { getDisplayEmail } from "@/lib/utils/display-email";
import { filterMembers, jobTitleOptions } from "./teamFilters";

type Status = "active" | "inactive" | "all";

// Radix Select items cannot have an empty value, so "all jobs" uses a sentinel.
const ALL_JOBS = "__all__";

export function TeamPeopleTab() {
  const { t } = useTranslation("team");
  const { data: members = [], isLoading, isError } = useTeamMembers();
  const [query, setQuery] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [status, setStatus] = useState<Status>("active");

  const filtered = useMemo(() => filterMembers(members, { query, jobTitle, status }), [members, query, jobTitle, status]);
  const jobs = useMemo(() => jobTitleOptions(members), [members]);

  const columns: DataColumn<TeamMemberRow>[] = [
    {
      id: "member",
      header: t("people.columns.member"),
      priority: "primary",
      cell: (m) => (
        <div className="flex items-center gap-3">
          <Avatar name={m.full_name} fallback={getInitials(m.full_name)} src={m.avatar_url ?? undefined} />
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-semibold text-foreground">{m.full_name}</span>
            <span className="truncate text-xs font-normal text-muted-foreground">{getDisplayEmail(m.email) ?? t("people.noEmail")}</span>
          </div>
        </div>
      ),
    },
    {
      id: "job",
      header: t("people.columns.jobTitle"),
      mobileLabel: t("people.columns.jobTitle"),
      cell: (m) => m.job_title ?? <span className="text-muted-foreground">{t("people.noJobTitle")}</span>,
    },
    {
      id: "access",
      header: t("people.columns.access"),
      mobileLabel: t("people.columns.access"),
      cell: (m) => <Badge variant={m.is_owner ? "warning" : "info"}>{t(`roles.${m.role_value}.name`)}</Badge>,
    },
    {
      id: "status",
      header: t("people.columns.status"),
      mobileLabel: t("people.columns.status"),
      cell: (m) =>
        isActiveMember(m) ? <Badge variant="success">{t("status.active")}</Badge> : <Badge>{t("status.inactive")}</Badge>,
    },
    {
      id: "open",
      header: <span className="sr-only">{t("people.columns.actions")}</span>,
      priority: "secondary",
      cell: (m) => (
        <Link
          to={`/team/${m.user_id}/dados`}
          aria-label={t("people.openMember", { name: m.full_name })}
          className="inline-flex min-h-11 items-center rounded-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t("people.open")}
        </Link>
      ),
    },
  ];

  return (
    <section className="flex flex-col overflow-hidden rounded-card border border-border bg-card">
      <div className="flex flex-col gap-3 border-b border-border p-4 md:flex-row md:flex-wrap md:items-end">
        <SearchField
          className="flex-1 md:min-w-64"
          label={t("people.searchLabel")}
          placeholder={t("people.searchPlaceholder")}
          clearLabel={t("people.clearSearch")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery("")}
        />
        <Select
          className="md:w-56"
          label={t("people.jobFilterLabel")}
          value={jobTitle || ALL_JOBS}
          onValueChange={(v) => setJobTitle(v === ALL_JOBS ? "" : v)}
          options={[{ value: ALL_JOBS, label: t("people.allJobs") }, ...jobs.map((j) => ({ value: j, label: j }))]}
        />
        <SegmentedTabs
          label={t("people.statusLabel")}
          value={status}
          onValueChange={(v) => setStatus(v as Status)}
          items={(["active", "inactive", "all"] as const).map((v) => ({ value: v, label: t(`people.statusFilter.${v}`) }))}
        />
      </div>
      {isError ? (
        <p role="alert" className="p-6 text-sm text-destructive">{t("people.error")}</p>
      ) : (
        <ResponsiveDataView
          className="[&_table]:rounded-none [&_table]:border-0 [&_.akuna-data-view__card]:rounded-none [&_.akuna-data-view__card]:border-0 [&_.akuna-data-view__card]:shadow-none"
          items={filtered}
          columns={columns}
          loading={isLoading}
          loadingLabel={t("people.loading")}
          getItemId={(m) => m.user_id}
          getItemLabel={(m) => m.full_name}
          emptyContent={<EmptyState title={t("people.empty.title")} description={t("people.empty.description")} />}
        />
      )}
    </section>
  );
}
